import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Avatar,
  Menu,
  MenuItem, 
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider
} from "@mui/material";
import { 
  Login as LoginIcon, 
  WbSunny, 
  Nightlight, 
  Dashboard, 
  LogoutSharp as LogoutIcon,
  Menu as MenuIcon,
  Home,
  PriceChange,
  Description,
  ContactSupport,
  AdminPanelSettings as AdminIcon
} from "@mui/icons-material";
import { Link, useLocation } from "react-router-dom";
import { useTheme as useCustomTheme } from "../theme/useTheme";
import { useAuth } from "../context/AuthContext";
import Logo from "./Logo";
import { useState } from "react";

export default function Navbar() {
  const { mode, setMode } = useCustomTheme();
  const { user, isAuthenticated, logout } = useAuth();
  const location = useLocation();

  // State for menus
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navigationItems = [
    { label: 'Home', path: '/', icon: <Home /> },
    { label: 'Pricing', path: '/pricing', icon: <PriceChange /> },
    { label: 'API Documentation', path: '/docs', icon: <Description /> },
    { label: 'Contact', path: '/contact', icon: <ContactSupport /> },
  ];

  function handleUserMenuOpen(event) {
    setUserMenuAnchor(event.currentTarget);
  }

  function handleUserMenuClose() {
    setUserMenuAnchor(null);
  }

  function handleLogout() {
    handleUserMenuClose();
    setMobileMenuOpen(false);
    logout();
  }

  function handleMobileMenuToggle() {
    setMobileMenuOpen(!mobileMenuOpen);
  }

  function handleMobileMenuClose() {
    setMobileMenuOpen(false);
  }

  const mobileDrawer = (
    <Drawer
      anchor="right"
      open={mobileMenuOpen}
      onClose={handleMobileMenuClose}
      PaperProps={{
        sx: {
          width: 240,
          bgcolor: 'background.paper',
        }
      }}
    >
      <List sx={{ pt: 1 }}>
        {navigationItems.map((item) => (
          <ListItem 
            key={item.path}
            component={Link}
            to={item.path}
            onClick={handleMobileMenuClose}
            selected={location.pathname === item.path}
            sx={{ 
              color: 'text.primary',
              '&.Mui-selected': {
                bgcolor: 'action.selected'
              }
            }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              {item.icon}
            </ListItemIcon>
            <ListItemText primary={item.label} />
          </ListItem>
        ))}
        
        <Divider sx={{ my: 1 }} />
        
        {isAuthenticated() ? (
          <>
            <ListItem 
              component={Link}
              to="/dashboard"
              onClick={handleMobileMenuClose}
              selected={location.pathname === '/dashboard'}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <Dashboard />
              </ListItemIcon>
              <ListItemText primary="Dashboard" />
            </ListItem>
            {user?.role === 'admin' && (
              <ListItem 
                component={Link}
                to="/admin"
                onClick={handleMobileMenuClose}
                selected={location.pathname === '/admin'}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <AdminIcon />
                </ListItemIcon>
                <ListItemText primary="Administration" />
              </ListItem>
            )}
            <ListItem button onClick={handleLogout}>
              <ListItemIcon sx={{ minWidth: 40 }}>
                <LogoutIcon />
              </ListItemIcon>
              <ListItemText primary="Logout" />
            </ListItem>
          </>
        ) : (
          <ListItem 
            component={Link}
            to="/login"
            onClick={handleMobileMenuClose}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              <LoginIcon />
            </ListItemIcon>
            <ListItemText primary="Sign In" />
          </ListItem>
        )}
      </List>
    </Drawer>
  );

  return (
    <AppBar position="sticky" elevation={1}>
      <Toolbar>
        <Box sx={{ display: "flex", alignItems: "center", flexGrow: 1 }}>
          <Link to="/">
            <Logo
              width="2.2rem"
              showText={false}
              className="logo"
              svgcolor="white"
            />
          </Link>
          <Typography variant="h6" component="div" sx={{ display: { xs: 'none', sm: 'block' } }}>
            EUDR API
          </Typography>
        </Box>

        {/* Desktop Navigation */}
        <Box sx={{ display: { xs: 'none', sm: 'flex' }, gap: 1, alignItems: "center" }}>
          {navigationItems.map((item) => (
            <Button 
              key={item.path}
              color="inherit" 
              component={Link} 
              to={item.path}
            >
              {item.label}
            </Button>
          ))}

          <IconButton
            color="inherit"
            aria-label="toggle theme"
            title={mode === "light" ? "Switch to Dark Mode" : "Switch to Light Mode"}
            onClick={() => setMode(mode === "light" ? "dark" : "light")}
          >
            {mode === "light" ? <Nightlight /> : <WbSunny />}
          </IconButton>

          {isAuthenticated() ? (
            <>
              <IconButton
                onClick={handleUserMenuOpen}
                sx={{ marginLeft: "1rem", borderRadius: "10px" }}
                title={user?.username || "User menu"}
              >
                <Avatar
                  alt={user?.username || "User"}
                  src={user?.avatar}
                  sx={{
                    width: 32,
                    height: 32,
                    backgroundColor: mode === "light" ? "background.paper" : "default",
                    color: mode === "light" ? "primary.main" : "#ffffff",
                  }}
                >
                  {user?.username?.[0]?.toUpperCase() || "U"}
                </Avatar>
                <Typography 
                  variant="span" 
                  component="div"
                  style={{ 
                    fontSize: "0.9rem",  
                    marginLeft: "0.5rem", 
                    color: "white" 
                  }}
                >
                  {user?.username}
                </Typography>
              </IconButton>

              <Menu
                anchorEl={userMenuAnchor}
                open={Boolean(userMenuAnchor)}
                onClose={handleUserMenuClose}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "right",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
              >
                <MenuItem
                  component={Link}
                  to="/dashboard"
                  onClick={handleUserMenuClose}
                >
                  <Dashboard sx={{ marginRight: "0.5rem" }} />
                  Dashboard
                </MenuItem>
                {user?.role === 'admin' && (
                  <MenuItem
                    component={Link}
                    to="/admin"
                    onClick={handleUserMenuClose}
                  >
                    <AdminIcon sx={{ marginRight: "0.5rem" }} />
                    Administration
                  </MenuItem>
                )}
                <MenuItem onClick={handleLogout}>
                  <LogoutIcon sx={{ marginRight: "0.5rem" }} />
                  Logout
                </MenuItem>
              </Menu>
            </>
          ) : (
            <Link
              title="Sign in"
              to="/login"
              style={{ marginLeft: "1rem" }}
              className="auth-link"
            >
              <Avatar
                alt="User"
                sx={{
                  width: 32,
                  height: 32,
                  backgroundColor: mode === "light" ? "background.paper" : "default",
                }}
              >
                <LoginIcon
                  sx={{
                    color: mode === "light" ? "primary.main" : "white",
                    fontSize: "1.2rem",
                  }}
                />
              </Avatar>
            </Link>
          )}
        </Box>

        {/* Mobile Navigation */}
        <Box sx={{ display: { xs: 'flex', sm: 'none' }, gap: 1, alignItems: "center" }}>
          <IconButton
            color="inherit"
            aria-label="toggle theme"
            onClick={() => setMode(mode === "light" ? "dark" : "light")}
          >
            {mode === "light" ? <Nightlight /> : <WbSunny />}
          </IconButton>

          {isAuthenticated() && (
            <Avatar
              alt={user?.username || "User"}
              src={user?.avatar}
              sx={{
                width: 32,
                height: 32,
                backgroundColor: mode === "light" ? "background.paper" : "default",
                color: mode === "light" ? "primary.main" : "#ffffff",
              }}
            >
              {user?.username?.[0]?.toUpperCase() || "U"}
            </Avatar>
          )}

          <IconButton
            color="inherit"
            aria-label="open menu"
            onClick={handleMobileMenuToggle}
            edge="end"
          >
            <MenuIcon />
          </IconButton>
        </Box>

        {mobileDrawer}
      </Toolbar>
    </AppBar>
  );
}
